import { Box, Button, Pagination } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { LocalOfferOutlined } from '@mui/icons-material';
import { Product_Bool_Exp, Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import { useProductsByCompanyLazyQuery } from '../../queries/__generated__/productsByCompany.generated';
import { RoutePaths } from '../../routes/AppRoutes';
import { currentContextAtom } from '../../state';
import TopAppBar from '../../common/components/TopAppBar';
import { canEditProductAtom } from '../../company/state';
import { ProductFragment } from '../products';
import ProductSearchBar from '../common/ProductSearchBar';
import { PRODUCT_NAME_TO_OPTIMIZE_WHERE, productFiltersAtom, ProductListTab, productListTabAtom } from './state';
import ProductList from './ProductList';
import ProductNameToOptimizeList from './ProductNameToOptimizeList';
import ProductNameOptimizationFloatingCard from './ProductNameOptimizationFloatingCard';
import ProductListTabs from './ProductListTabs';

const PAGE_SIZE = 20;

export default function ProductListPage(): React.JSX.Element {
  const { companyId } = useAtomValue(currentContextAtom);
  const [productFilters, setProductFilters] = useAtom(productFiltersAtom);
  const [tab, setTab] = useAtom(productListTabAtom);
  const { t } = useTranslation(['common', 'products']);
  const [getCompanyProducts, { data: productsByCompanyQuery, loading }] = useProductsByCompanyLazyQuery();
  const [products, setProducts] = useState<ProductFragment[]>([]);
  const [companyProductSubFamilies, setCompanyProductSubFamilies] = useState<Product_Sub_Family_Name_Enum[]>([]);
  const [totalProductsCount, setTotalProductsCount] = useState(0);
  const canEdit = useAtomValue(canEditProductAtom);

  const numberOfProductString = totalProductsCount ? ` (${totalProductsCount})` : '';

  const handlePageChange = useCallback((newPage: number): void => {
    setProductFilters({ ...productFilters, page: newPage });
  }, [productFilters]);

  const handleTabChange = (newTab: ProductListTab): void => {
    setTab(newTab);
    setProductFilters({ ...productFilters, page: 1 });
  };

  useEffect(() => {
    const queryProductWhere: Product_Bool_Exp = {
      company_id: {
        _eq: companyId,
      },
    };

    queryProductWhere.sub_family = productFilters.productFamilies.length ? { _in: productFilters.productFamilies } : undefined;
    queryProductWhere._or = productFilters.query
      ? [
        { name: { _ilike: `%${productFilters.query}%` } },
        { base_units: { gtin: { _ilike: `%${productFilters.query}%` } } },
      ]
      : undefined;

    const availabilityCondition = productFilters.availability === 'ALL' ? undefined : { availability: { _eq: productFilters.availability } };
    queryProductWhere.base_units = availabilityCondition;
    queryProductWhere.product_naming_validation = tab === 'NAME_TO_OPTIMIZE' ? PRODUCT_NAME_TO_OPTIMIZE_WHERE : undefined;

    const offset = (productFilters.page - 1) * PAGE_SIZE;

    getCompanyProducts({
      variables: {
        where: queryProductWhere,
        baseUnitsWhere: availabilityCondition,
        limit: PAGE_SIZE,
        companyId,
        offset,
      },
    });
  }, [productFilters, tab]);

  useEffect(() => {
    if (productsByCompanyQuery?.product) {
      setTotalProductsCount(productsByCompanyQuery.product_aggregate.aggregate!.count);
      setProducts(productsByCompanyQuery.product);
      setCompanyProductSubFamilies(productsByCompanyQuery.sub_families.map((s) => s.sub_family as Product_Sub_Family_Name_Enum));
    }
  }, [productsByCompanyQuery]);

  return (
    <>
      <TopAppBar
        configuration="small"
        backTo={RoutePaths.NAVIGATION_MAIN}
        shouldCheckHistory={false}
        title={`${t('common:product_other')}${numberOfProductString}`}
        sx={{
          position: 'fixed',
        }}
      />
      <Box
        sx={{
          height: {
            lg: 'calc(100% - 48px)',
          },
          overflow: {
            lg: 'auto',
          },
          mt: {
            xs: 6,
            lg: 0,
          },
          mb: {
            xs: 10,
            lg: 0,
          },
          p: {
            xs: 1,
            lg: 3,
          },
        }}
      >
        <>
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 1, flex: 1 }}>
              <LocalOfferOutlined height={28} width={28} />
              <Typography variant="titleLarge">
                {t('common:product', { count: totalProductsCount })} ({totalProductsCount})
              </Typography>
            </Box>
            {!!products.length && canEdit && (
            <Button
              variant="contained"
              component={Link}
              startIcon={<AddIcon />}
              to={RoutePaths.PRODUCT_ADD_INFO}
              state={{ origin: RoutePaths.COMPANY_PRODUCTS }}
              sx={{
                position: {
                  xs: 'fixed',
                  lg: 'initial',
                },
                width: {
                  xs: 'calc(100% - 32px)',
                  lg: 'initial',
                },
                maxWidth: 320,
                bottom: 32,
                transform: {
                  xs: 'translate(-50%, 0)',
                  lg: 'unset',
                },
                left: '50%',
                zIndex: 10,
              }}
            >
              {t('products:add_a_product')}
            </Button>
            )}
          </Box>
          <ProductSearchBar
            productFamiliesOptions={companyProductSubFamilies}
            defaultValues={productFilters}
            sx={{
              mt: 1,
              mb: canEdit ? 1.5 : 4,
            }}
          />
          {canEdit && <ProductListTabs tab={tab} onChange={handleTabChange} />}
          {
          // eslint-disable-next-line no-nested-ternary
                 loading
                   ? (
                     <Box
                       sx={{
                         display: 'flex',
                         flexDirection: 'column',
                         alignItems: 'center',
                         justifyContent: 'center',
                         p: 4,
                       }}
                     >
                       <CircularProgress size={128} />
                     </Box>
                   )
                   : tab === 'NAME_TO_OPTIMIZE'
                     ? <ProductNameToOptimizeList products={products} />
                     : <ProductList products={products} />
}
          <Pagination
            color="primary"
            page={productFilters.page}
            count={Math.ceil(totalProductsCount / PAGE_SIZE)}
            onChange={(event, newPage) => handlePageChange(newPage)}
            sx={{ my: 3, justifyContent: 'center', display: 'flex' }}
          />
        </>

      </Box>
      {canEdit && tab === 'ALL' && (
        <ProductNameOptimizationFloatingCard onAction={() => handleTabChange('NAME_TO_OPTIMIZE')} />
      )}
    </>
  );
}
